import { FC } from "react";
import { Box, SegmentedControl, Select, Stack, Text } from "@mantine/core";
import { FilterComponentProps } from ".";
import { DollarRangeInputs, salesWindowCaption } from "./DollarRangeFilter";
import {
  commitPreviousSales,
  PREVIOUS_SALES_ALL,
  PREVIOUS_SALES_HAS,
  PREVIOUS_SALES_LABEL_MAP,
  PREVIOUS_SALES_NONE,
  PREVIOUS_SALES_WINDOW_LABEL_MAP,
  PREVIOUS_SALES_WINDOW_OPTIONS,
  PreviousSalesDraft,
  previousSalesDraftOf,
  previousSalesImpossibleRange,
  PreviousSalesSelection,
  previousSalesSelection,
  previousSalesState,
  previousSalesWindowMonths,
} from "../consts";

/**
 * EIS-1091 + EIS-1093 + EIS-1097: the MERGED "Previous Sales" map filter.
 *
 * A THREE-WAY segmented control (All / Has previous sales / No previous sales)
 * with PROGRESSIVE DISCLOSURE:
 *
 *   All  -> nothing revealed; the filter is off
 *   Has  -> dollar range AND window select
 *   No   -> window select ONLY -- a dollar range is meaningless with no sales,
 *           and the two-row design this replaces let the user ask for
 *           "no previous sales, between $1,000 and $5,000 of them".
 *
 * WHY NOT the multi-select switch group every other filter row uses (EIS-1088
 * shipped one here): "has" and "none" are exact complements, so two independent
 * on/off switches had FOUR states for a three-state question. The fourth,
 * both-off, could only ever mean "show nothing": an empty map that reads as a
 * bug and invites a support ticket. A segmented control has exactly as many
 * states as the question has answers. The Source row next door is a genuine
 * 4-way multi-select and must KEEP its switches.
 *
 * AC2 -- DRAFT PRESERVATION. The committed value is a discriminated union, and
 * `{mode:'all'}` / `{mode:'none'}` have nowhere to keep a dollar range. So the
 * editor writes BOTH on every change: the union (what the wire and the badge
 * read) AND `previousSalesDraft` (what the inputs read). Switching
 * Has -> No -> Has therefore returns the user to the numbers they typed instead
 * of silently resetting them to $1. The draft is inert -- nothing reads it to
 * build a request -- so it cannot make the map disagree with the row.
 *
 * There is no arming flag any more (EIS-1093 had one): the SEGMENT is the
 * arming. All is off, Has/No are on, and the panel only pushes the control to
 * the map when Apply is pressed, so an in-progress range still never reaches the
 * server early.
 *
 * Test ids:
 *   map-filter-previous-sales-segmented                the mode control
 *   map-filter-previous-sales-option-{all|has|none}    each mode option's label
 *   map-filter-previous-sales-caption                  the plain-English summary
 *   map-filter-previous-sales-window                   the window Select input
 *   map-filter-previous-sales-window-option-{3|6|12|24}  each window option
 *   map-filter-previous-sales-window-caption           the dated window caption
 *   map-filter-previous-sales-range                    the range block (Has only)
 *   map-filter-previous-sales-min-input / -max-input   the two bounds
 *   map-filter-previous-sales-no-upper-bound-caption   "$1 and up"
 *   map-filter-previous-sales-impossible-range-warning "no account can match"
 * Mantine renders one `input[type=radio][value=...]` per segment inside the mode
 * control, which is what "is this option selected?" should be asserted on.
 */
const PreviousSalesFilter: FC<FilterComponentProps> = ({
  onFilterControlChange,
  filterControl
}) => {
  // Route the incoming value through the shared migration so that a persisted
  // EIS-1088 array, an EIS-1091 scalar, or junk still renders a VALID state.
  // Handing SegmentedControl a value that is not one of its options would leave
  // the control with nothing highlighted -- a silent third failure mode.
  const state = previousSalesState(filterControl?.previousSales);
  const mode = state.mode;

  // The inputs always read the DRAFT, and the draft mirrors the union whenever
  // Has is selected -- so what is on screen under Has is exactly what will go on
  // the wire, and what is remembered under All/No is exactly what was last
  // typed.
  const stored = previousSalesDraftOf(filterControl?.previousSalesDraft);
  const draft: PreviousSalesDraft = mode === PREVIOUS_SALES_HAS
    ? { windowMonths: state.windowMonths, min: state.min, max: state.max }
    : {
      windowMonths: mode === PREVIOUS_SALES_NONE
        ? state.windowMonths
        : stored.windowMonths,
      min: stored.min,
      max: stored.max,
    };

  /** Write the union AND the draft together -- they must never drift apart. */
  const commit = (
    nextMode: PreviousSalesSelection,
    nextDraft: PreviousSalesDraft = draft,
  ) => onFilterControlChange({
    ...filterControl,
    previousSales: commitPreviousSales(nextMode, nextDraft),
    previousSalesDraft: nextDraft,
  });

  const windowLabel = PREVIOUS_SALES_WINDOW_LABEL_MAP.get(draft.windowMonths)
    ?? `last ${draft.windowMonths} months`;

  // EIS-1097 round 3. A finite, well-typed range can still be self-contradictory
  // ("$1 to $0" under a segment that already means "> $0"; "$5,000 to $100"),
  // and the old behaviour was a blank map with nothing anywhere saying why.
  //
  // The range is NOT corrected and Apply is NOT blocked. Clamping is banned by
  // the ticket, and blocking would leave the editor showing one question while
  // the map still answered the previous one -- and it would do nothing at all
  // for a blob that arrives already impossible, which never passes through
  // Apply. So the question is sent verbatim and the user is TOLD, here and on
  // the collapsed row (previousSalesSummary), what it is going to return.
  //
  // Read off the DRAFT, which under Has is exactly the committed union, so the
  // warning is on screen precisely when the impossible bounds are on the wire.
  const impossibleRange = mode === PREVIOUS_SALES_HAS
    ? previousSalesImpossibleRange(draft.min, draft.max)
    : null;

  return (
    <Box mt="xl" ml={10} mr={5} h="85%" style={{ overflowY: "auto" }}>
      <Stack gap="sm">
        <SegmentedControl
          fullWidth
          orientation="vertical"
          size="md"
          color="#4F7D9C"
          value={mode}
          data-testid="map-filter-previous-sales-segmented"
          onChange={(v) => commit(previousSalesSelection(v))}
          data={Array.from(PREVIOUS_SALES_LABEL_MAP.entries()).map(([key, label]) => ({
            value: key,
            label: (
              <Text
                component="span"
                fz="sm"
                fw="bold"
                data-testid={`map-filter-previous-sales-option-${key}`}>
                {label}
              </Text>
            ),
          }))}
        />
        {/* Say out loud what the current segment means, so nobody reads the
            default as a half-configured filter and nobody has to infer that the
            window applies to "No" as well. */}
        <Text
          fz="xs"
          c="#6B6B6B"
          ta="center"
          data-testid="map-filter-previous-sales-caption">
          {mode === PREVIOUS_SALES_ALL
            ? "Showing every practitioner, with or without previous sales."
            : `Showing only practitioners with ${
              mode === PREVIOUS_SALES_HAS ? "at least one" : "no"
            } sale in the ${windowLabel}.`}
        </Text>

        {/* PROGRESSIVE DISCLOSURE. Under All there is nothing to configure, so
            rendering a disabled window select would just be furniture that
            implies the filter is doing something. */}
        {mode === PREVIOUS_SALES_ALL ? null : (
          <Stack gap="sm" mt="md">
            <Select
              label="in the"
              value={String(draft.windowMonths)}
              allowDeselect={false}
              withCheckIcon={false}
              data-testid="map-filter-previous-sales-window"
              // The drawer sits at zIndex 5001; Mantine's popover default is 300,
              // so an un-raised dropdown would open BEHIND the drawer and be
              // unclickable. Portalled (not inline) because this Box scrolls.
              comboboxProps={{ withinPortal: true, zIndex: 5002 }}
              onChange={(v) => commit(mode, {
                ...draft,
                windowMonths: previousSalesWindowMonths(v),
              })}
              renderOption={({ option }) => (
                <Text
                  component="span"
                  fz="sm"
                  data-testid={`map-filter-previous-sales-window-option-${option.value}`}>
                  {option.label}
                </Text>
              )}
              data={PREVIOUS_SALES_WINDOW_OPTIONS.map((months) => ({
                value: String(months),
                label: PREVIOUS_SALES_WINDOW_LABEL_MAP.get(months) as string,
              }))}
            />
            {/* The dates the window actually covers, COMPUTED -- a user asking
                "which 6 months?" must not have to work it out. */}
            <Text
              fz="xs"
              c="#6B6B6B"
              data-testid="map-filter-previous-sales-window-caption">
              {salesWindowCaption(draft.windowMonths)}
            </Text>

            {/* The dollar range belongs to Has ONLY. Under No it is not hidden,
                it is ABSENT -- see the union in ../consts. */}
            {mode === PREVIOUS_SALES_HAS ? (
              <DollarRangeInputs
                min={draft.min}
                max={draft.max}
                testIdPrefix="map-filter-previous-sales"
                onChange={({ min, max }) => commit(mode, { ...draft, min, max })} />
            ) : null}

            {/* Same slot, same styling as the warning EIS-1093 kept under its
                range inputs -- that one had to go because the trap inverted and
                its words became false, not because the idiom was wrong. This one
                lives HERE rather than inside DollarRangeInputs because "$0 or
                less is impossible" is a fact about the Has segment, not about
                dollar ranges in general. */}
            {impossibleRange ? (
              <Text
                c="#FF9D00"
                ta="center"
                fw="bold"
                w={300}
                mx="auto"
                data-testid="map-filter-previous-sales-impossible-range-warning">
                {impossibleRange}
              </Text>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default PreviousSalesFilter;
